/**
 * Script to backfill embeddings for existing approved policies
 * Run this manually after deploying the RAG system
 */

import { supabase } from '@/integrations/supabase/client';

export interface BackfillProgress {
  total: number;
  processed: number;
  succeeded: number;
  failed: number;
  errors: Array<{ policyId: string; error: string }>;
}

/**
 * Trigger embedding generation for a single policy
 * Calls the Edge Function to generate embeddings
 */
async function triggerEmbeddingGeneration(policyId: string): Promise<boolean> {
  try {
    // Get API key from localStorage (BYOK)
    const apiKey = localStorage.getItem('openai_api_key');
    
    if (!apiKey) {
      throw new Error('OpenAI API key is required. Please configure it in Settings → AI & Integration.');
    }
    
    // Use Supabase's function invoke method (handles auth automatically)
    const { data, error } = await supabase.functions.invoke('generate-policy-embeddings', {
      body: {
        policy_id: policyId,
        action: 'create',
        api_key: apiKey
      }
    });

    if (error) {
      throw new Error(error.message || 'Failed to invoke Edge Function');
    }

    if (data && !data.success) {
      throw new Error(data.error || 'Edge Function returned error');
    }

    console.log(`✓ Generated embeddings for policy ${policyId}:`, data);
    return true;
  } catch (error: any) {
    console.error(`✗ Failed to generate embeddings for policy ${policyId}:`, error);
    throw error;
  }
}

/**
 * Backfill embeddings for all approved policies in the company
 * 
 * @param companyId - Company ID to backfill (if not provided, uses current user's company)
 * @param onProgress - Callback for progress updates
 * @returns Final progress report
 */
export async function backfillEmbeddings(
  companyId?: string,
  onProgress?: (progress: BackfillProgress) => void
): Promise<BackfillProgress> {
  const progress: BackfillProgress = {
    total: 0,
    processed: 0,
    succeeded: 0,
    failed: 0,
    errors: []
  };

  try {
    // If no companyId provided, get from current user
    let targetCompanyId = companyId;
    
    if (!targetCompanyId) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .single();
      
      if (!profile?.company_id) {
        throw new Error('No company ID found for current user');
      }
      
      targetCompanyId = profile.company_id;
    }

    // Fetch all approved policies for the company
    const { data: policies, error } = await supabase
      .from('policies')
      .select('id, title')
      .eq('company_id', targetCompanyId)
      .eq('status', 'approved');

    if (error) {
      throw error;
    }

    if (!policies || policies.length === 0) {
      console.log('No approved policies found to backfill');
      return progress;
    }

    progress.total = policies.length;
    console.log(`\nStarting backfill for ${progress.total} approved policies...\n`);

    // Process each policy
    for (const policy of policies) {
      try {
        console.log(`Processing: ${policy.title} (${policy.id})`);
        await triggerEmbeddingGeneration(policy.id);
        progress.succeeded++;
      } catch (error: any) {
        progress.failed++;
        progress.errors.push({
          policyId: policy.id,
          error: error.message
        });
      } finally {
        progress.processed++;
        
        // Call progress callback
        if (onProgress) {
          onProgress({ ...progress });
        }

        // Add delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    console.log('\n=== Backfill Complete ===');
    console.log(`Total: ${progress.total}`);
    console.log(`Succeeded: ${progress.succeeded}`);
    console.log(`Failed: ${progress.failed}`);
    
    if (progress.errors.length > 0) {
      console.log('\nErrors:');
      progress.errors.forEach(({ policyId, error }) => {
        console.log(`  - ${policyId}: ${error}`);
      });
    }

    return progress;
  } catch (error: any) {
    console.error('Backfill failed:', error);
    throw error;
  }
}

/**
 * Check which approved policies are missing embeddings
 * Useful for identifying policies that need backfilling
 */
export async function checkMissingEmbeddings(companyId?: string): Promise<{
  total: number;
  withEmbeddings: number;
  missing: number;
  missingPolicies: Array<{ id: string; title: string }>;
}> {
  try {
    // If no companyId provided, get from current user
    let targetCompanyId = companyId;
    
    if (!targetCompanyId) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .single();
      
      if (!profile?.company_id) {
        throw new Error('No company ID found for current user');
      }
      
      targetCompanyId = profile.company_id;
    }

    // Get all approved policies
    const { data: policies, error: policiesError } = await supabase
      .from('policies')
      .select('id, title')
      .eq('company_id', targetCompanyId)
      .eq('status', 'approved');

    if (policiesError) {
      throw policiesError;
    }

    if (!policies || policies.length === 0) {
      return {
        total: 0,
        withEmbeddings: 0,
        missing: 0,
        missingPolicies: []
      };
    }

    // Get policies that have embeddings
    const { data: embeddedPolicies, error: embeddingsError } = await supabase
      .from('policy_embeddings')
      .select('policy_id')
      .eq('company_id', targetCompanyId);

    if (embeddingsError) {
      throw embeddingsError;
    }

    const embeddedPolicyIds = new Set(
      embeddedPolicies?.map(e => e.policy_id) || []
    );

    const missingPolicies = policies.filter(
      p => !embeddedPolicyIds.has(p.id)
    );

    return {
      total: policies.length,
      withEmbeddings: embeddedPolicyIds.size,
      missing: missingPolicies.length,
      missingPolicies
    };
  } catch (error: any) {
    console.error('Error checking missing embeddings:', error);
    throw error;
  }
}

